import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PaymentForm = ({ formData, setFormData, errors, pricingBreakdown }) => {
  const paymentMethods = [
    { value: 'upi', label: 'UPI Payment', description: 'PhonePe, Google Pay, Paytm' },
    { value: 'card', label: 'Credit/Debit Card', description: 'Visa, Mastercard, RuPay' },
    { value: 'netbanking', label: 'Net Banking', description: 'All major banks' },
    { value: 'cash', label: 'Cash on Pickup', description: 'Pay driver directly' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm space-y-4 md:space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="CreditCard" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground">
            Payment Details
          </h2>
          <p className="text-sm text-muted-foreground">
            Review pricing and complete payment
          </p>
        </div>
      </div>
      <div className="bg-gradient-journey rounded-lg p-4 md:p-6 text-white space-y-3">
        <h3 className="text-base md:text-lg font-semibold">Pricing Breakdown</h3>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">Base Fare</span>
            <span className="font-semibold">{formatCurrency(pricingBreakdown?.baseFare)}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">Distance Charges ({pricingBreakdown?.distance} km)</span>
            <span className="font-semibold">{formatCurrency(pricingBreakdown?.distanceCharges)}</span>
          </div>
          
          {pricingBreakdown?.nightCharges > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/80">Night Charges (10 PM - 6 AM)</span>
              <span className="font-semibold">{formatCurrency(pricingBreakdown?.nightCharges)}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">GST (5%)</span>
            <span className="font-semibold">{formatCurrency(pricingBreakdown?.gst)}</span>
          </div>
          
          {pricingBreakdown?.discount > 0 && (
            <div className="flex items-center justify-between text-sm text-accent">
              <span>Discount Applied</span>
              <span className="font-semibold">-{formatCurrency(pricingBreakdown?.discount)}</span>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-white/20">
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold">Total Amount</span>
            <span className="text-2xl font-bold">{formatCurrency(pricingBreakdown?.total)}</span>
          </div>
        </div>
      </div>
      <Select
        label="Payment Method"
        description="Choose your preferred payment option"
        options={paymentMethods}
        value={formData?.paymentMethod}
        onChange={(value) => handleInputChange('paymentMethod', value)}
        error={errors?.paymentMethod}
        required
      />
      {formData?.paymentMethod === 'upi' && (
        <div className="p-4 bg-muted/50 rounded-lg space-y-4">
          <Input
            label="UPI ID"
            type="text"
            placeholder="yourname@upi"
            value={formData?.upiId}
            onChange={(e) => handleInputChange('upiId', e?.target?.value)}
            error={errors?.upiId}
            required
          />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} className="text-primary" />
            <span>Secure UPI payment gateway</span>
          </div>
        </div>
      )}
      {formData?.paymentMethod === 'card' && (
        <div className="p-4 bg-muted/50 rounded-lg space-y-4">
          <Input
            label="Card Number"
            type="text"
            placeholder="1234 5678 9012 3456"
            value={formData?.cardNumber}
            onChange={(e) => handleInputChange('cardNumber', e?.target?.value)}
            error={errors?.cardNumber}
            required
            maxLength={19}
          />
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Expiry Date"
              type="text"
              placeholder="MM/YY"
              value={formData?.cardExpiry}
              onChange={(e) => handleInputChange('cardExpiry', e?.target?.value)}
              error={errors?.cardExpiry}
              required
              maxLength={5}
            />
            
            <Input
              label="CVV"
              type="text"
              placeholder="123"
              value={formData?.cardCvv}
              onChange={(e) => handleInputChange('cardCvv', e?.target?.value)}
              error={errors?.cardCvv}
              required
              maxLength={3}
            />
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Lock" size={16} className="text-primary" />
            <span>Your card details are encrypted and secure</span>
          </div>
        </div>
      )}
      {formData?.paymentMethod === 'cash' && (
        <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-1">
                Cash Payment Terms
              </h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Pay exact amount to driver before journey starts</li>
                <li>• Advance booking amount: ₹500 (non-refundable)</li>
                <li>• Balance payment in cash to driver</li>
                <li>• Request receipt from driver for your records</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        <Checkbox
          label="I agree to the Terms and Conditions"
          checked={formData?.agreeTerms}
          onChange={(e) => handleInputChange('agreeTerms', e?.target?.checked)}
          error={errors?.agreeTerms}
          required
        />

        <Checkbox
          label="I have read and accept the Cancellation Policy"
          description="Free cancellation up to 24 hours before pickup"
          checked={formData?.agreeCancellation}
          onChange={(e) => handleInputChange('agreeCancellation', e?.target?.checked)}
          error={errors?.agreeCancellation}
          required
        />
      </div>
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Your Booking is Protected
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Check" size={14} className="text-primary" />
                <span>Verified drivers</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Check" size={14} className="text-primary" />
                <span>24/7 support</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Check" size={14} className="text-primary" />
                <span>Secure payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Check" size={14} className="text-primary" />
                <span>Trip insurance</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;